import { memo, useCallback, useMemo, useState } from 'react';

import { Confirm, Modal, TextClamp } from '@components/common';
import Table from '@components/common/Table';

import useMutateChangeMonitoringErrorMessage
	from '@hooks/networkElementConfigurations/useMutateChangeMonitoringErrorMessage';
import {
	useQueryChangeMonitoringStatusesHistory,
} from '@hooks/networkElementConfigurations/useQueryChangeMonitoringStatusesHistory';

import {
	ISortBy,
	ITablePagination,
	ITableSort,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { HEADER_FONT_SIZE } from '../constants';

import { getColumns } from './utils';

import styles from './ChangeMonitoringHistoryStatuses.module.scss';

interface IChangeMonitoringHistoryStatuses {
	updateId: string;
	value: string;
	name: string;
}

const TABLE_HEIGHT = '75%';

const ChangeMonitoringHistoryStatuses = ({
	updateId,
	value,
	name,
}: IChangeMonitoringHistoryStatuses) => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [isErrorModal, setIsErrorModal] = useState(false);

	const {
		statusesHistory,
		isLoadingStatusesHistory,
		countOfRecords,
	} = useQueryChangeMonitoringStatusesHistory({
		updateId,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
	});

	const {
		getErrorMessage,
		isLoadingChangeMonitoringErrorMessage,
		errorMessage,
	} = useMutateChangeMonitoringErrorMessage();

	const openErrorMessageModal = useCallback(() => {
		setIsErrorModal(true);
		getErrorMessage({ updateId });
	}, [setIsErrorModal, getErrorMessage, updateId]);

	const columns = useMemo(() => getColumns({
		openErrorMessageModal,
	}), [openErrorMessageModal]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPage(defaultMeta.page);
		setPageSize(pageSize);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const closeErrorMessageModal = useCallback(() => {
		setIsErrorModal(false);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<div className={styles.title}>{name}</div>
				<div className={styles.detail}>
					<div className={styles.name}>N:</div>
					<div className={styles.value}>{statusesHistory.length}</div>
				</div>
				<div className={styles.detail}>
					<div className={styles.name}>Value:</div>
					<TextClamp
						clampedTextClassname={styles.value}
						fontSize={HEADER_FONT_SIZE}
						text={value}
					/>
				</div>
			</div>
			<Modal
				isLoading={isLoadingChangeMonitoringErrorMessage}
				isOpen={isErrorModal}
				onClose={closeErrorMessageModal}
				title='Error'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					negativeBtnText='Close'
					question={errorMessage}
				/>
			</Modal>
			<Table
				columns={columns}
				data={statusesHistory}
				isLoading={isLoadingStatusesHistory}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight={TABLE_HEIGHT}
				pagination={pagination}
				searchThreshold={0}
				sort={sort}
			/>
		</div>
	);
};

export default memo(ChangeMonitoringHistoryStatuses);
